// Arcturian-Marker: Protokoll statt Textwand.
//
// Der Server hängt einer Arcturian-Zustellung einen Klartext-Marker voran, damit
// der EMPFANGENDE AGENT ohne Inbox-Abfrage antworten kann (Product #4431
// `p-5cde1ac88a89`, Abschnitt „Protokoll und Transport"). Für einen Menschen im
// Portal ist derselbe Marker eine Zeile technischer Anker vor dem eigentlichen
// Satz — Alex' Screenshot 118435 zeigt das Ergebnis: die Frage geht zwischen
// action_id, in_reply_to und authority unter.
//
// Diese Funktion trennt beides: den Satz für den Leser, die Anker für die
// Diagnose.
//
// FAIL-CLOSED: Passt die Form nicht exakt, wird `null` zurückgegeben und die
// bisherige Textdarstellung bleibt. Eine Karte, die einen Anker falsch zuordnet,
// ist schlimmer als eine Zeile Rohtext — bei action_id und authority ist die
// Zuordnung der ganze Zweck.

export interface ArcturianEnvelope {
  /** Die sprechende Persona — heute immer „Arcturian". */
  persona: string;
  /** Auftraggeber im Klartext („Alexander Popovic"). */
  onBehalfOf: string;
  /** Kanal, über den zugestellt wurde („IACP"). */
  channel: string;
  /** Der eigentliche Text — das, was ein Mensch lesen will. */
  body: string;
  /** Bekannte Anker, in Reihenfolge des Auftretens. */
  fields: Array<{ key: string; value: string }>;
  /** Antwortanweisung an den Zielagenten. Für Menschen Rauschen, deshalb
   *  getrennt geführt und in der Oberfläche standardmäßig verborgen. */
  replySpec?: string;
}

/** Trenner zwischen den Segmenten des Markers. Mittelpunkt mit Leerzeichen. */
const SEP = ' · ';

/**
 * Zerlegt den Markerkopf an ` · `, aber NICHT innerhalb runder Klammern.
 *
 * Nötig wegen `reply=send_message(to_session=…, in_reply_to=…, event_type=…)`:
 * dessen Inhalt trägt Kommata und Semikola, und ein künftiger Zusatz könnte
 * auch einen Mittelpunkt enthalten. Ohne Klammertiefe würde der Wert
 * zerschnitten und der Rest als eigene Felder missdeutet.
 */
function splitSegments(head: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < head.length; i++) {
    const ch = head[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (depth === 0 && head.startsWith(SEP, i)) {
      out.push(head.slice(start, i));
      i += SEP.length - 1;
      start = i + 1;
    }
  }
  out.push(head.slice(start));
  return out.map((x) => x.trim()).filter(Boolean);
}

/** Findet die schliessende Klammer des Markers — klammertief, damit die
 *  runde Klammer in `reply=…` nicht fälschlich als Ende gilt. */
function markerEnd(text: string): number {
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === ']' && depth === 0) return i;
  }
  return -1;
}

/**
 * Entfernt die Anhänge, die der Zustellweg hinter den Inhalt hängt.
 *
 * `agent_comm.send` plättet die Nachricht auf EINE Zeile und ergänzt danach
 * einen Protokoll-Hinweis für den empfangenden Agenten, bei großen Nachrichten
 * zusätzlich einen Verweis auf die Inbox (AppDevV2, 2026-08-06, aus dem
 * Generator gelesen). Ohne dieses Abschneiden landet die Handlungsanweisung
 * mitten im Satz, den ein Mensch lesen soll — genau die Vermischung, die diese
 * Karte auflösen soll.
 */
function stripPaneSuffixes(raw: string): string {
  let out = raw;
  // Bekannte Anhänge, jeweils ab ihrer öffnenden Klammer bis zum Ende: sie
  // stehen laut Generator immer am Schluss.
  for (const marker of ['[IACP-PROTOCOL:', '[STRUCTURED CONTEXT', '[MESSAGE TRUNCATED']) {
    const at = out.indexOf(marker);
    if (at >= 0) out = out.slice(0, at);
  }
  return out.trim();
}

const ON_BEHALF = /^im Auftrag von\s+(.+)$/i;

export function parseArcturianEnvelope(raw: string | null | undefined): ArcturianEnvelope | null {
  if (!raw) return null;
  // Die IACP-Hülle ist mal da und mal gestrippt — beide Formen zulassen.
  const text = raw.replace(/^\s*\[IACP:[^\]]*\]\s*/i, '').trimStart();
  if (!text.startsWith('[')) return null;

  const end = markerEnd(text);
  if (end < 0) return null;

  const segments = splitSegments(text.slice(1, end));
  // Mindestens Persona + Auftraggeber + Kanal. Weniger ist kein Arcturian-Marker.
  if (segments.length < 3) return null;

  const persona = segments[0];
  const behalf = ON_BEHALF.exec(segments[1]);
  if (!behalf) return null;
  const channel = segments[2];
  // Kanal ist ein kurzes Kürzel; steht dort ein `key=value`, ist die Form anders
  // als angenommen und wir steigen aus, statt zu raten.
  if (!channel || channel.includes('=')) return null;

  const fields: Array<{ key: string; value: string }> = [];
  let replySpec: string | undefined;
  for (const seg of segments.slice(3)) {
    const eq = seg.indexOf('=');
    if (eq <= 0) continue;             // unbekanntes Segment ohne Schlüssel: ignorieren
    const key = seg.slice(0, eq).trim();
    const value = seg.slice(eq + 1).trim();
    if (key === 'reply') replySpec = value;
    else fields.push({ key, value });  // unbekannte Schlüssel bleiben erhalten
  }

  const body = stripPaneSuffixes(text.slice(end + 1));
  if (!body) return null;              // ein Marker ohne Inhalt ist keine Nachricht

  return { persona, onBehalfOf: behalf[1].trim(), channel, body, fields, replySpec };
}

/** Kurzform langer Anker für die Anzeige: `act_fd078a…1a4643`. Die Mitte trägt
 *  keine Information, die Enden schon (Präfix sagt den Typ, Suffix erlaubt den
 *  Abgleich mit einem Log). */
export function shortenAnchor(value: string, keep = 6): string {
  if (value.length <= keep * 2 + 3) return value;
  return `${value.slice(0, value.indexOf('_') + 1 + keep)}…${value.slice(-keep)}`;
}
